import { useState, useEffect } from 'react'
import { wishlistAPI } from '../services/api'
import { formatCurrency, formatDate } from '../utils/helpers'
import WishlistForm from '../components/WishlistForm'

const PRIORITY_CONFIG = {
  high: { label: 'Tinggi', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  medium: { label: 'Sedang', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  low: { label: 'Rendah', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
}

export default function Wishlist() {
  const [wishlists, setWishlists] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('active')
  const [showForm, setShowForm] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [purchaseConfirm, setPurchaseConfirm] = useState(null)

  useEffect(() => {
    fetchWishlists()
  }, [activeTab])

  const fetchWishlists = async () => {
    setLoading(true)
    try {
      const data = await wishlistAPI.getAll(activeTab)
      setWishlists(data.wishlists || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      await wishlistAPI.delete(id)
      setDeleteConfirm(null)
      fetchWishlists()
    } catch (err) {
      console.error(err)
    }
  }

  const handleMarkPurchased = async (id) => {
    try {
      await wishlistAPI.markPurchased(id)
      setPurchaseConfirm(null)
      fetchWishlists()
    } catch (err) {
      console.error(err)
    }
  }

  const totalWishlistValue = wishlists
    .filter(w => w.status === 'active')
    .reduce((sum, w) => sum + w.price, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Wishlist</h2>
        <button
          onClick={() => { setEditItem(null); setShowForm(true) }}
          className="btn-primary text-sm"
        >
          + Tambah
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { value: 'active', label: '🎯 Inginkan' },
          { value: 'purchased', label: '✅ Sudah Dibeli' },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === tab.value
                ? 'bg-primary text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Total Value */}
      {activeTab === 'active' && wishlists.length > 0 && (
        <div className="card bg-purple-50 border border-purple-100">
          <p className="text-purple-600 text-sm">Total Nilai Wishlist</p>
          <p className="text-2xl font-bold text-purple-800">{formatCurrency(totalWishlistValue)}</p>
        </div>
      )}

      {/* Wishlist Items */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="card animate-pulse">
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-gray-200 rounded-xl" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : wishlists.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-4xl mb-2">🎁</p>
          <p className="text-gray-500">
            {activeTab === 'active' ? 'Belum ada wishlist' : 'Belum ada item yang dibeli'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {wishlists.map((item) => {
            const priority = PRIORITY_CONFIG[item.priority]
            return (
              <div key={item.id} className={`card ${activeTab === 'purchased' ? 'opacity-80' : ''}`}>
                <div className="flex gap-4">
                  {/* Image or Placeholder */}
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl">🛍️</span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`font-semibold text-gray-900 ${activeTab === 'purchased' ? 'line-through text-gray-500' : ''}`}>
                        {item.name}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${priority.color}`}>
                        {priority.label}
                      </span>
                    </div>

                    <p className="text-primary font-bold mt-0.5">{formatCurrency(item.price)}</p>

                    {item.notes && (
                      <p className="text-gray-500 text-xs mt-1 truncate">{item.notes}</p>
                    )}

                    {item.purchased_at && (
                      <p className="text-gray-400 text-xs mt-1">
                        Dibeli: {formatDate(item.purchased_at)}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-2">
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          🔗 Lihat Produk
                        </a>
                      )}

                      {activeTab === 'active' && (
                        <div className="flex gap-1 ml-auto">
                          <button
                            onClick={() => setPurchaseConfirm(item)}
                            className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                          >
                            ✅ Beli
                          </button>
                          <button
                            onClick={() => { setEditItem(item); setShowForm(true) }}
                            className="p-1 text-gray-400 hover:text-primary transition-colors"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(item)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            🗑️
                          </button>
                        </div>
                      )}

                      {activeTab === 'purchased' && (
                        <button
                          onClick={() => setDeleteConfirm(item)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors ml-auto"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Wishlist Form */}
      {showForm && (
        <WishlistForm
          item={editItem}
          onClose={() => { setShowForm(false); setEditItem(null) }}
          onSuccess={() => { setShowForm(false); setEditItem(null); fetchWishlists() }}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-lg mb-2">Hapus dari Wishlist?</h3>
            <p className="text-gray-500 text-sm mb-1">{deleteConfirm.name}</p>
            <p className="font-semibold text-gray-900 mb-6">{formatCurrency(deleteConfirm.price)}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">
                Batal
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="btn-danger flex-1">
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Confirm */}
      {purchaseConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-lg mb-2">Tandai Sudah Dibeli?</h3>
            <p className="text-gray-500 text-sm mb-1">{purchaseConfirm.name}</p>
            <p className="font-semibold text-gray-900 mb-6">{formatCurrency(purchaseConfirm.price)}</p>
            <div className="flex gap-3">
              <button onClick={() => setPurchaseConfirm(null)} className="btn-secondary flex-1">
                Batal
              </button>
              <button
                onClick={() => handleMarkPurchased(purchaseConfirm.id)}
                className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                ✅ Ya, Sudah Dibeli
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
